import React from "react";
import { Users, HeartHandshake, Leaf, Scale, Star, Rocket } from "lucide-react";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HandShake from "../media/Handshake.avif";
import Rewarding from '../media/Partner.avif'
import Together from "../media/Together.avif";
import Img1 from "../media/img1.avif"
import Img2 from "../media/img2.avif"
import Img3 from "../media/img3.avif"
import Img4 from "../media/img4.avif"
import PhilosophyImg from "../media/philosophy.avif"

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const OurPhilosophy = () => {
  return (
    <>
        < Header />
        <main className="bg-white text-gray-800 overflow-hidden">
            {/* Hero Section */}
            <section className="relative mt-16 md:mt-24 flex items-center justify-center h-[60vh] bg-cover bg-center" style = {{backgroundImage: `url(${PhilosophyImg})`}}>
                <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                
                ></motion.div>

                <motion.h1
                className="relative text-white text-5xl md:text-6xl font-bold text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                >
                Our Philosophy
                </motion.h1>
            </section>

            {/* Intro Section */}
            <section className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center gap-10">
                <motion.div
                className="md:w-1/2"
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                >
                <h2 className="text-2xl font-bold text-brand mb-4">
                    A People-Powered Philosophy
                </h2>
                <p className="text-gray-700 leading-relaxed">
                    At Yking, our people are at the heart of everything we do. From those who create our garments to those who wear them, we care deeply, and that care shapes our entire approach. Diverse, passionate, talented, and driven, our team is the vital force that runs through every aspect of our operation.
                </p>
                </motion.div>

                <motion.div
                className="md:w-1/2"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                >
                <img
                    src={HandShake}
                    alt="Handshake"
                    className="rounded-xl shadow-md"
                />
                </motion.div>
            </section>

            {/* Vision Section */}
            <motion.section
                className="bg-brand text-white py-16"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
            >
                <div className="max-w-5xl mx-auto text-center px-6">
                <h2 className="text-3xl font-bold mb-3">Our Vision</h2>
                <p className="text-lg leading-relaxed">
                    To be the trusted leader in the market, delivering innovative fashion solutions with transparency and integrity.
                </p>
                </div>
            </motion.section>

            {/* Mission Section */}
            <section className="bg-orange-100 py-20">
                <div className="max-w-6xl mx-auto px-6">
                <motion.h2
                    className="text-3xl font-bold text-center text-brand mb-10"
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    Our Mission
                </motion.h2>

                <div className="grid md:grid-cols-3 gap-8">
                    {[1, 2, 3].map((num, index) => (
                    <motion.div
                        key={num}
                        className="bg-white rounded-xl shadow-md p-6"
                        variants={fadeInUp}
                        initial="hidden"
                        whileInView="visible"
                        transition={{ delay: index * 0.2 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="text-brand text-3xl font-bold mb-2">
                        {`0${num}`}
                        </h3>
                        <p className="text-gray-700">
                        {num === 1 &&
                            "To deliver exceptional service and innovative designs to Yking clients, sourced in the most ethical way."}
                        {num === 2 &&
                            "Offering all Yking employees equal opportunities for personal and professional growth within a dynamic and supportive work environment."}
                        {num === 3 &&
                            "To make the world a better place by supporting those in need and creating positive, lasting change through compassion and innovation."}
                        </p>
                    </motion.div>
                    ))}
                </div>
                </div>
            </section>

            {/* Core Values */}
            <motion.section
                className="max-w-6xl flex flex-col mx-auto px-6 py-16"
                initial="hidden"
                whileInView="visible"
                variants={fadeInUp}
                viewport={{ once: true }}
            >
                <h2 className="text-3xl font-serif font-bold text-black mb-8">
                    Our Core Values
                </h2>

                <div className="flex flex-col md:flex-row">
                    {/* Left paragraph */}
                    <div className="md:w-1/2 pr-8 mb-8 md:mb-0">
                        <p className="text-gray-700 leading-relaxed">
                            It's critical to never lose sight of the values and tenets upon
                            which we were built. We are proud of our beliefs and work hard to
                            live up to them in all we do, from sourcing practices to fashion
                            designs.
                        </p>
                    </div>

                    {/* Right column: Values with icons */}
                    <div className="md:w-1/2 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ul className="space-y-4 text-gray-700">
                            <li className="flex items-center gap-3">
                                <Users className="text-brand w-5 h-5" />
                                <span>Transparency, Collaboration & Teamwork</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <HeartHandshake className="text-brand w-5 h-5" />
                                <span>People First</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Leaf className="text-brand w-5 h-5" />
                                <span>Sustainability, Ethics & Compliance</span>
                            </li>
                        </ul>

                        <ul className="space-y-4 text-gray-700">
                            <li className="flex items-center gap-3">
                                <Scale className="text-brand w-5 h-5" />
                                <span>Trust, Integrity & Ethics</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Star className="text-brand w-5 h-5" />
                                <span>Prioritizing Customer Service</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Rocket className="text-brand w-5 h-5" />
                                <span>Entrepreneurial Spirit</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </motion.section>

            {/* Rewarding Team Spirit */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center gap-10">
                <motion.div
                    className="md:w-1/2"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                >
                    <img
                    src={Rewarding}
                    alt="Team Celebration"
                    className="rounded-xl shadow-md"
                    />
                </motion.div>

                <motion.div
                    className="md:w-1/2"
                    variants={fadeInUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    <h2 className="text-3xl font-bold text-black font-serif mb-4">
                    Rewarding Team Spirit
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                    We put a lot of effort into making sure our team members receive the recognition they deserve since without their hard work, we wouldn’t exist. Reward and recognition are the foundation of team spirit, and our summer parties and award ceremonies serve as our little token of appreciation.
                    </p>
                </motion.div>
                </div>
            </section>

            
            {/* Closing Banner */}
            <motion.section
                className="relative h-[50vh] bg-cover bg-center flex flex-col items-center justify-center"
                style={{ backgroundImage: `url(${Together})` }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}
            >
                <div className="absolute inset-0 bg-black bg-opacity-60"></div>
                <motion.h2
                className="relative z-10 font-serif text-white text-4xl md:text-5xl font-semibold text-center max-w-7xl"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }}
                >
                Together, we're more than the sum of our parts.
                </motion.h2>
                <motion.h3
                className="relative z-10 mt-6 text-white text-base md:text-lg font-semibold text-center max-w-7xl"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }}
                >
                Our strength lies in our diversity. From fashion enthusiasts to supply chain experts and design innovators, we’re proud to have a dynamic and eclectic team driving us forward
                </motion.h3>
                <motion.a href="/our-story" className="relative z-10 border text-white mt-4 shadow-lg px-4 py-2">Our Story</motion.a>
            </motion.section>

            <motion.section className="bg-brand text-white py-20 text-center" initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1 }}
                viewport={{ once: true }}>
                        <motion.h2 className="text-4xl font-serif font-bold mb-10" initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }} >Principles. Passion. Empowerment.</motion.h2>
                        <motion.div className="flex flex-wrap justify-center gap-40" initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }} >
                        <motion.img src={Img4} initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }} className="w-60 h-60 object-cover rounded-lg shadow-lg" alt="" />
                        <motion.img src={Img3} initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }} className="w-60 h-60 object-cover rounded-lg shadow-lg" alt="" />
                        <motion.img src={Img2} initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }} className="w-60 h-60 object-cover rounded-lg shadow-lg" alt="" />
                        <motion.img src={Img1} initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                viewport={{ once: true }} className="w-60 h-60 object-cover rounded-lg shadow-lg" alt="" />
                        </motion.div>
            </motion.section>
        </main>
        <Footer />
    </>
  );
};

export default OurPhilosophy;
